import { useEffect, useState } from 'react';
import { NumericFormat } from 'react-number-format';
import { useDebounce } from 'react-use';
import Input from '@components/Input';

import type { NumberFormatConfig } from '../types';

interface NumberInputProps {
  values: string[];
  onUpdate: (values: string[]) => void;
  placeholder?: string;
  format?: NumberFormatConfig;
}

const INT32_MAX = 2_147_483_647; // OpenSearch `int` upper bound (2^31 - 1)
const FLOAT32_MAX = 3.4028235e38; // OpenSearch `float` ceiling

export function NumberInput({ values, onUpdate, placeholder = 'Set a value', format }: NumberInputProps) {
  const [local, setLocal] = useState(values[0] ?? '');

  useEffect(() => {
    setLocal(values[0] ?? '');
  }, [values]);

  useDebounce(() => onUpdate(local ? [local] : []), 300, [local]);

  const MAX = format?.decimalScale ? FLOAT32_MAX : INT32_MAX;
  const MIN = format?.decimalScale ? -FLOAT32_MAX : -INT32_MAX;

  return (
    <NumericFormat
      customInput={Input}
      className="bg-white dark:bg-white dark:text-gray-900"
      allowNegative={false}
      allowLeadingZeros={false}
      isAllowed={({ value, floatValue }) => {
        // Reject keystrokes/pastes that produce a leading zero ("00", "007")
        if (/^0\d/.test(value)) return false;

        return floatValue === undefined || (floatValue >= MIN && floatValue <= MAX);
      }}
      decimalScale={format?.decimalScale ?? 0}
      thousandSeparator={format?.thousandSeparator ?? false}
      suffix={format?.suffix ?? ''}
      placeholder={placeholder}
      value={local}
      valueIsNumericString
      onValueChange={({ value }) => setLocal(value)}
    />
  );
}
